import { useMemo, useState } from "react";
import { ChevronRight, Folder, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import { downloadFile } from "../../lib/files";
import { isAdmin, useAuth } from "../../context/AuthContext";
import { FileRow } from "./FileRow";
import { FileViewer } from "./FileViewer";

const byName = (a, b) => a.localeCompare(b, "es", { sensitivity: "base", numeric: true });

// REQ-372..380: the client's Drive folder — sub-folders first, then files, alphabetical; breadcrumb back.
const level = (files, path) => {
  const folders = new Set();
  const here = [];
  files.forEach((f) => {
    if (path.some((p, i) => f.folder[i] !== p)) return;
    if (f.folder.length > path.length) folders.add(f.folder[path.length]);
    else if (f.folder.length === path.length) here.push(f);
  });
  return { folders: [...folders].sort(byName), files: here.sort((a, b) => byName(a.original_filename, b.original_filename)) };
};

const Breadcrumb = ({ clientName, path, onGo }) => (
  <nav className="flex flex-wrap items-center gap-1 text-sm" data-testid="folder-breadcrumb">
    {[clientName, ...path].map((name, i) => (
      <span key={`${i}-${name}`} className="inline-flex items-center gap-1">
        {i > 0 && <ChevronRight className="h-3.5 w-3.5 text-[#9bb0cc]" />}
        {i === path.length ? <span className="font-bold text-[#182235]" data-testid={`breadcrumb-${i}`}>{name}</span>
          : <button type="button" onClick={() => onGo(i)} className="font-semibold text-[#0f5ef7] hover:underline" data-testid={`breadcrumb-${i}`}>{name}</button>}
      </span>
    ))}
  </nav>
);

export const DriveFolderView = ({ clientName, files }) => {
  const { user } = useAuth();
  const [path, setPath] = useState([]);
  const [viewing, setViewing] = useState(null);
  const view = useMemo(() => level(files, path), [files, path]);
  const onDownload = (file) => downloadFile(file).catch(() => toast.error("No se pudo descargar el archivo, intenta de nuevo"));

  return (
    <section className="tr-card p-5 sm:p-6" data-testid="expediente-section">
      <div className="mb-4">
        <div className="tr-eyebrow mb-1">Expediente</div>
        <h2 className="text-base font-bold text-[#182235] md:text-lg">Documentos del cliente</h2>
      </div>
      {files.length === 0 ? (
        <p className="rounded-xl bg-[#f5f8fd] px-4 py-6 text-center text-sm font-semibold text-[#5b667a]" data-testid="folder-empty-client">Este cliente aún no tiene documentos en Drive.</p>
      ) : (
        <div className="space-y-3">
          <Breadcrumb clientName={clientName} path={path} onGo={(i) => setPath(path.slice(0, i))} />
          <ul className="space-y-2" data-testid="folder-list">
            {view.folders.map((name, i) => (
              <li key={name}>
                <button type="button" onClick={() => setPath([...path, name])} data-testid={`folder-item-${i}`}
                  className="flex w-full items-center gap-3 rounded-[12px] border border-[#e8edf5] bg-white p-3 text-left text-sm font-semibold text-[#182235] transition-colors hover:border-[#9bb7e8] hover:bg-[#f5f8fd]">
                  <Folder className="h-5 w-5 shrink-0 text-[#5b8def]" /> <span className="truncate">{name}</span>
                </button>
              </li>
            ))}
            {view.files.map((f) => <FileRow key={f.id} file={f} onView={setViewing} onDownload={onDownload} canDownload={isAdmin(user)} />)}
            {!view.folders.length && !view.files.length && (
              <li className="flex items-center gap-2 px-3 py-4 text-sm text-[#5b667a]" data-testid="folder-empty"><FolderOpen className="h-4 w-4" /> Esta carpeta está vacía</li>
            )}
          </ul>
        </div>
      )}
      <FileViewer file={viewing} onClose={() => setViewing(null)} canDownload={isAdmin(user)} />
    </section>
  );
};
